// Testify: the QA widget (/testify skill harness) as a guest in Scout. The widget is a self-contained
// script served from /testify/testify.js; it owns its own fetches to /api/testify/* (the one sanctioned
// exception to "fetch lives in api/", because the script is foreign and self-disabling). This module is
// the pure part: the routes the widget's page mode pushes through, the config shape, and a loader that
// resolves to null — never throws — when the script is missing. Scout must render fine without it.

export type TestifyView = 'board' | 'dashboard'
export type TestifyType = 'Defect' | 'Question' | 'Feedback'

export interface TestifyApi {
  version: string
  /** Keeps the live open-defect badge on a nav node that carries a `.tfy-badge` slot. */
  mount(node: Element): unknown
  open(view?: TestifyView): void
  /** Renders the workspace INTO a page node; `ticket` (TFY-21 or a guid) opens that ticket's drawer. */
  render(node: Element, opts: { view: TestifyView; compact?: boolean; ticket?: string }): unknown
  newTicket(type: TestifyType): void
}

export interface TestifyConfig {
  base: string
  user: { name: string }
  headers: Record<string, string>
  statuses: [string, string][]
  /** Page mode: Board and Stats are Scout routes, not panes of an overlay. */
  navigate(view: TestifyView): void
  /** The drawer opened (or closed: null), so the ticket can live in the URL as a deep link. */
  onTicketOpen(idOrNumber: string | null): void
}

declare global {
  interface Window {
    Testify?: TestifyApi
    TESTIFY?: TestifyConfig
  }
}

/** The headers the widget sends on every call, read at CALL time. A snapshot taken when the widget mounted
 *  outlived the token it copied (a re-login, a rotation) and the widget kept sending the dead one — Don's
 *  console on 2026-09-23 held twenty "/badge -> 401" while Scout itself ran fine on the live session. The
 *  config exposes this through a getter, so every fetch asks for the token that is current NOW. */
export function testifyHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export const TESTIFY_SCRIPT = '/testify/testify.js'
export const TESTIFY_BASE = '/api/testify'

/** The board's columns, in order: the stock four plus the two the "work the board" rules need. */
export const TESTIFY_STATUSES: [string, string][] = [
  ['New', 'New'],
  ['InProgress', 'In progress'],
  ['NeedsClarity', 'Needs clarity'],
  ['OnHold', 'On hold'],
  ['Resolved', 'Resolved'],
  ['Closed', 'Closed'],
]

export const QUALITY_ROUTE = '/quality'
export const QUALITY_STATS_ROUTE = '/quality/stats'

/** Where the widget's Board / Stats buttons go. */
export function qualityRoute(view: TestifyView): string {
  return view === 'dashboard' ? QUALITY_STATS_ROUTE : QUALITY_ROUTE
}

/** Which view a quality route shows. */
export function qualityView(pathname: string): TestifyView {
  return pathname.replace(/\/+$/, '') === QUALITY_STATS_ROUTE ? 'dashboard' : 'board'
}

/** `?ticket=TFY-21` while a ticket is open; nothing when it closes. */
export function ticketSearch(idOrNumber: string | null): string {
  return idOrNumber ? `?ticket=${encodeURIComponent(idOrNumber)}` : ''
}

let loading: Promise<TestifyApi | null> | null = null
let settle: ((api: TestifyApi | null) => void) | null = null
const ready = new Promise<TestifyApi | null>((resolve) => {
  settle = resolve
})

/**
 * Injects the script once and resolves to the widget — or to null if there is no document, the file
 * 404s, or the script chose to disable itself. Call after the config is set; the widget reads
 * `window.TESTIFY` when it initialises.
 */
export function loadTestify(): Promise<TestifyApi | null> {
  if (loading) return loading
  if (typeof document === 'undefined') {
    loading = Promise.resolve(null)
  } else if (window.Testify) {
    loading = Promise.resolve(window.Testify)
  } else {
    loading = new Promise<TestifyApi | null>((resolve) => {
      const script = document.createElement('script')
      script.src = TESTIFY_SCRIPT
      script.async = true
      script.onload = () => resolve(window.Testify ?? null)
      script.onerror = () => resolve(null)
      document.head.appendChild(script)
    })
  }
  void loading.then((api) => settle?.(api))
  return loading
}

/**
 * Resolves once the shell has loaded the widget (a page may mount before the shell's effect runs, so
 * this waits for the load rather than starting one), or to null after `timeoutMs` so a page never
 * waits forever on a script that is not coming.
 */
export function whenTestify(timeoutMs = 15_000): Promise<TestifyApi | null> {
  return Promise.race([
    ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ])
}
